import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  RefObject,
} from 'react';
import { observer } from 'mobx-react';
import { message } from 'antd';

import GridBoards from '@/components/GridBoards';
import {
  useApps,
  useSiders,
  useWidgets,
  usePickers,
  useFolders,
  useShortcuts,
} from './hooks';
import { useDesktop, useProduct } from '@/store';
import { useWallpaper } from '../../Background/BgSet';
import LayoutBoxRight from './LayoutBoxRight';

import './index.less';
import { $t } from '@/Intl';
const useBdKey = (deps: any): [number, () => void] => {
  const [bdKey, setBdKey] = useState(0);
  const isMounted = useRef(false);
  const updateBdKey = () => {
    setBdKey(Math.random());
  };

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      updateBdKey();
    }
  }, [deps]);

  return [bdKey, updateBdKey];
};

/**
 * 返回不同型号设备layoutbox的属性，包括列数，行数，宽， 高， 导航栏sider位置
 */
const useLayoutProps = (): {
  col: number;
  row: number;
  width: number;
  height: number;
  siderBounds: {
    siderX?: number;
    siderY?: number;
  };
} => {
  const [product] = useProduct();
  switch (product) {
    case 'gxv3370':
    case 'gxv3380':
    case 'gxv3480':
      return {
        col: 6,
        row: 4,
        width: 800,
        height: 428,
        siderBounds: {
          siderX: 5,
        },
      };
    case 'gxv3350':
    case 'gxv3450':
      return {
        col: 6,
        row: 3,
        width: 728,
        height: 378,
        siderBounds: {
          siderX: 5,
        },
      };
    case 'gxv3470':
      return {
        col: 4,
        row: 6,
        width: 422,
        height: 582,
        siderBounds: {
          siderY: 5,
        },
      };
    case 'gac2570':
      return {
        col: 5,
        row: 3,
        width: 700,
        height: 378,
        siderBounds: {
          siderX: 4,
        },
      };
  }
};

export interface LayoutBoxRef {
  GB: RefObject<GridBoards>;
  screen: number;
  updateBdKey: () => void;
  siderBounds: LayoutBoxSiderbounds;
}

export default observer(
  forwardRef<LayoutBoxRef>((_, ref) => {
    const GB = useRef<GridBoards>(null);
    const [desktop] = useDesktop();
    const [product] = useProduct();
    const { col, row, width, height, siderBounds } = useLayoutProps();
    const [screen, setScreen] = useState(desktop.homeIndex!);
    const [bdKey, updateBdKey] = useBdKey(screen);
    const [apps, updateApps] = useApps(screen, siderBounds);
    const [widgets, updateWidgets] = useWidgets(screen, siderBounds);
    const [shortcuts, updateShortcuts] = useShortcuts(screen, siderBounds);
    const [siders, updateSiders] = useSiders(siderBounds);
    const [pickers, updatePickers] = usePickers(screen, siderBounds);
    const { folders, updateFolders, createFolder, insertAppToFolderIfValid } =
      useFolders(screen, siderBounds);
    const wallpaper = useWallpaper();

    useImperativeHandle(ref, () => ({ GB, screen, updateBdKey, siderBounds }), [
      GB,
      screen,
      updateBdKey,
      siderBounds,
    ]);

    /**
     * GridMain 做成了非受控的组件，为了减少复杂度，在换页操作时，直接remount整个组件
     */
    return (
      <div className={`layout-box layout-${product}`}>
        <GridBoards
          ref={GB}
          key={bdKey}
          rootClassName='layout-box-left'
          width={width}
          height={height}
          col={col}
          row={row}
          defaultData={[
            ...apps,
            ...siders,
            ...widgets,
            ...folders,
            ...shortcuts,
          ]}
          pickerList={pickers}
          pickPostion={
            typeof siderBounds.siderX !== 'undefined' ? 'bottom' : 'right'
          }
          mainBg={wallpaper}
          onChange={(value) => {
            updateApps(value);
            updateSiders(value);
            updateWidgets(value);
            updateFolders(value);
            updateShortcuts(value);
          }}
          onPickerChange={(pickers) => {
            updatePickers(pickers);
          }}
          onBeforeRemove={(item) => {
            if (item?.child?.length) {
              message.error($t('CANNOT_REMOVE_FOLDER'));
              return false;
            }
            return true;
          }}
          onBeforeDrop={(...arg) => {
            return insertAppToFolderIfValid(...arg);
          }}
        />
        <LayoutBoxRight
          screen={screen}
          onSetScreen={setScreen}
          handleAddDir={() => {
            GB.current?.insertItem(
              createFolder(
                {
                  screen,
                  child: [],
                  title: $t('UNNAMED_FOLDER'),
                },
                siderBounds,
              ),
            );
          }}
        />
      </div>
    );
  }),
);
